import React, { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { RotateCcw, Loader2 } from "lucide-react";

import { Button } from "../ui/button";
import AiCopilot from "../AiCopilot";
import TicketNotes from "../TicketNotes";
import CallButton from "../CallButton";
import BotaoDeVoz from "../BotaoDeVoz";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import TicketOptionsMenu from "../TicketOptionsMenu";
import ResolverTicketModal from "../ResolverTicketModal";
import ReabrirTicketModal, {
	carregarMotivosDeReabertura,
} from "../ReabrirTicketModal";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import usePrefTickets from "../../hooks/usePrefTickets";
import useLayoutTickets from "../../hooks/useLayoutTickets";
import useAtalhoDeTeclado from "../../hooks/useAtalhoDeTeclado";
import AjudaDeAtalhos from "../AjudaDeAtalhos";
import { ACOES_PADRAO } from "./acoesDaBarra";

const TicketActionButtons = ({ ticket }) => {
	const navigate = useNavigate();
	const [loading, setLoading] = useState(false);
	// Encerrar pelo cabeçalho passa pelo mesmo modal da linha da lista: motivo,
	// observação e a escolha de perguntar ao cliente. Antes o botão daqui
	// fechava direto — a mesma ação com dois comportamentos, e o motivo de
	// encerramento só era gravado quem encerrasse pela lista.
	const [resolvendo, setResolvendo] = useState(false);
	// Reabrir passa a perguntar por quê — mas só se a empresa cadastrou
	// motivos. Sem nenhum cadastrado o botão continua sendo um clique só.
	const [reabrindo, setReabrindo] = useState(false);
	const { user } = useContext(AuthContext);
	// Quais atalhos esta pessoa quer ver. O menu ⋮ continua com tudo — esconder
	// um botão nunca esconde a ação.
	const [acoesBarra] = usePrefTickets("acoesBarra", ACOES_PADRAO);
	// O layout diz se o Esc devolve a lista — só o Foco a esconde.
	const layout = useLayoutTickets();
	const naBarra = (id) => acoesBarra.includes(id);

	const handleUpdateTicketStatus = async (e, status, userId) => {
		setLoading(true);
		try {
			await api.put(`/tickets/${ticket.id}`, {
				status: status,
				userId: userId || null,
			});

			setLoading(false);
			if (status === "open") {
				navigate(`/tickets/${ticket.id}`);
			} else {
				navigate("/tickets");
			}
		} catch (err) {
			setLoading(false);
			toastError(err);
		}
	};

	const pedirReabertura = async () => {
		const motivos = await carregarMotivosDeReabertura();

		if (!motivos.length) {
			handleUpdateTicketStatus(null, "open", user?.id);
			return;
		}

		setReabrindo(true);
	};

	// Alt+e de encerrar: abre o modal de encerramento. Com Alt porque a tecla
	// solta transformaria um clique perdido fora do campo em atendimento a
	// caminho de ser encerrado. Só com o atendimento em aberto — atalho que às
	// vezes funciona é pior do que atalho nenhum.
	useAtalhoDeTeclado(
		"e",
		() => setResolvendo(true),
		ticket?.status === "open",
		{ alt: true }
	);

	return (
		<div className="ml-auto flex flex-wrap items-center justify-end gap-1.5">
			<ResolverTicketModal
				open={resolvendo}
				onClose={() => setResolvendo(false)}
				ticket={ticket}
				onResolved={() => navigate("/tickets")}
			/>
			<ReabrirTicketModal
				open={reabrindo}
				onClose={() => setReabrindo(false)}
				ticket={ticket}
				aoReabrir={() => navigate(`/tickets/${ticket.id}`)}
			/>
			{/* Dois caminhos de voz podem coexistir: a Wavoip (que é serviço
			    contratado à parte e só aparece com token) e a conexão de voz
			    própria (whatsmeow pelo sidecar), que só aparece quando existe
			    uma. Na prática a empresa tem um dos dois; quem tiver os dois vê
			    os dois, cada um com a sua dica. */}
			{naBarra("ligar") && <CallButton ticket={ticket} />}
			{naBarra("ligar") && (
				<BotaoDeVoz
					numero={ticket?.contact?.number}
					nome={ticket?.contact?.name}
					isGroup={ticket?.isGroup}
				/>
			)}
			{naBarra("notas") && <TicketNotes ticketId={ticket.id} />}
			{naBarra("copiloto") && (
				<AiCopilot
					ticketId={ticket.id}
					onUsarTexto={(texto) =>
						document.dispatchEvent(
							new CustomEvent("copilot:usar-texto", { detail: texto })
						)
					}
				/>
			)}
			{/* O menu sai de dentro do bloco de "open": pausar o robô e ajustar a
			    automação valem em qualquer situação, e é justamente na conversa
			    ainda AGUARDANDO que se quer calar o robô — antes de assumir. */}
			<AjudaDeAtalhos
				ticketAberto
				foco={layout === "foco"}
				variante="botao"
			/>
			<TicketOptionsMenu ticket={ticket} />
			{ticket.status === "closed" && (
				<Button
					size="sm"
					variant="outline"
					disabled={loading}
					onClick={pedirReabertura}
				>
					{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
					{i18n.t("messagesList.header.buttons.reopen")}
				</Button>
			)}
			{ticket.status === "open" && (
				<>
					<Button
						size="sm"
						variant="outline"
						disabled={loading}
						onClick={(e) => handleUpdateTicketStatus(e, "pending", null)}
					>
						{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
						{i18n.t("messagesList.header.buttons.return")}
					</Button>
					<Button
						size="sm"
						disabled={loading}
						onClick={() => setResolvendo(true)}
					>
						{loading && <Loader2 className="h-4 w-4 animate-spin" />}
						{i18n.t("messagesList.header.buttons.resolve")}
					</Button>
				</>
			)}
			{ticket.status === "pending" && (
				<Button
					size="sm"
					disabled={loading}
					onClick={(e) => handleUpdateTicketStatus(e, "open", user?.id)}
				>
					{loading && <Loader2 className="h-4 w-4 animate-spin" />}
					{i18n.t("messagesList.header.buttons.accept")}
				</Button>
			)}
		</div>
	);
};

export default TicketActionButtons;
